import { UserServiceClient } from 'fitbeat.api-clients';
import { UserProfile } from 'fitbeat.gym-core';
import { ITargetMetricValues } from 'fitbeat.models';
import { UserTargetTypes, WeightTargetStep } from 'fitbeat.models';
import moment from 'moment-timezone';
import { NativeEventEmitter, NativeModules, Platform } from 'react-native';
import { PaywallPage } from '../auth';
import { BluetoothServiceSingleton, IBluetoothService } from '../HomeWorkouts';
import { openPaywallModalAction } from '../payments/redux/payments-actions';
import { UserProfileService } from '../services';
import { analyicsForAction } from '../services/google-analytics/analyicsForAction';
import { requestBodyMetricStatsSuccess } from '../Tracking/actions';
import { toggleBodyScanModal } from '../ui/redux';
import { toggleWeightTargetModal } from '../ui/redux';
import { Logger } from '../utils';
import { Permissions } from '../utils/Permissions';
import { requestWeightTargetInfo, requestWeightTargetInfoSuccess,
    resetTargetRequest, resetTargetRequestSuccess, setWeightTargetStep } from '../WeightTarget/actions';
import { weightTargetInfoFromGoalSelector } from '../WeightTarget/selectors';
import {
    BODY_SCALE_CAPTURE_METRIC_INFO,
    BODY_SCALE_ERROR,
    BODY_SCALE_ONBOARDING_START,
    BODY_SCALE_PROGRESS_UPDATE,
    BODY_SCALE_SCAN_START,
    DISCARD_BODY_SCALE_SCAN,
    NEXT_BODY_ASSESSMENT_QUESTION,
    PREVIOUS_BODY_ASSESSMENT_QUESTION,
    REMOVE_SCALE_FAILURE,
    REMOVE_SCALE_REQUEST,
    REMOVE_SCALE_SUCCESS,
    REQUEST_BODY_SCALE_SCAN_STATS,
    REQUEST_BODY_SCALE_SCAN_STATS_SUCCESS,
    REQUEST_FAILED,
    REQUEST_OVERRIDE_BODY_COMPOSITION_AND_CONDITIONING,
    REQUEST_OVERRIDE_BODY_COMPOSITION_AND_CONDITIONING_SUCCESS,
    REQUEST_SAVE_BODY_ASSESSMENT,
    REQUEST_SAVE_BODY_ASSESSMENT_SUCCESS,
    REQUEST_SAVE_BODY_SCALE_SCAN,
    REQUEST_SAVE_BODY_SCALE_SCAN_SUCCESS,
    SET_BODY_ASSESSMENT_ANSWER,
    SET_IS_SCAN_CONTINUING_FROM_WEIGHT_TARGET,
    SET_SCAN_PROGRESS,
    SET_TESTING_STUB,
    SHOW_BODY_TYPE_OPTIONS,
} from './actionTypes';
import { CONDITIONED_ATHLETE_ANSWERS } from './assessmentQuestionnair';

const maxBodyScaleHeight = 220;
const maxBodyScaleAge = 150;

export const setTestingStub = (useTestingStub) => {
    return {
        type: SET_TESTING_STUB,
        payload: {
            useTestingStub,
        },
    };
};

const bodyScaleScanStart = (userProfile) => {
    return {
        type: BODY_SCALE_SCAN_START,
        payload: {
            userProfile,
        },
        ...analyicsForAction(BODY_SCALE_SCAN_START, { initiated_from: 'tracking_health_tile' }),
    };
};

const requestBodyScaleScanStats = (rawScan) => {
    return {
        type: REQUEST_BODY_SCALE_SCAN_STATS,
        payload: {
            rawScan,
        },
    };
};
const requestBodyScaleScanStatsSuccess = (scaleScanStats) => {
    return {
        type: REQUEST_BODY_SCALE_SCAN_STATS_SUCCESS,
        payload: {
            ...scaleScanStats,
        },
        ...analyicsForAction(REQUEST_BODY_SCALE_SCAN_STATS_SUCCESS),
    };
};

export const requestSaveBodyScaleScan = () => {
    return {
        type: REQUEST_SAVE_BODY_SCALE_SCAN,
        ...analyicsForAction(REQUEST_SAVE_BODY_SCALE_SCAN),
    };
};

export const requestOverrideBodyCompositionAndConditioning = () => {
    return {
        type: REQUEST_OVERRIDE_BODY_COMPOSITION_AND_CONDITIONING,
        ...analyicsForAction(REQUEST_OVERRIDE_BODY_COMPOSITION_AND_CONDITIONING),
    };
};

export const requestOverrideBodyCompositionAndConditioningSuccess = (composition: string,
                                                                     athleteMode: CONDITIONED_ATHLETE_ANSWERS) => {
    return {
        type: REQUEST_OVERRIDE_BODY_COMPOSITION_AND_CONDITIONING_SUCCESS,
        payload: {
            composition,
            athleteMode,
        },
        ...analyicsForAction(REQUEST_OVERRIDE_BODY_COMPOSITION_AND_CONDITIONING_SUCCESS),
    };
};

export const requestSaveBodyScaleScanSuccess = (bodyMetricSet, shouldResetBodySacle = true) => {
    return {
        type: REQUEST_SAVE_BODY_SCALE_SCAN_SUCCESS,
        payload: {
            bodyMetricSet,
            shouldResetBodySacle,
        },
    };
};

export const requestSaveBodyAssessment = () => {
    return {
        type: REQUEST_SAVE_BODY_ASSESSMENT,
        ...analyicsForAction(REQUEST_SAVE_BODY_ASSESSMENT),
    };
};

export const requestSaveBodyAssessmentSuccess = (composition: string) => {
    return {
        type: REQUEST_SAVE_BODY_ASSESSMENT_SUCCESS,
        payload: {
            composition,
        },
    };
};

export const discardBodyScaleScan = () => {
    return {
        type: DISCARD_BODY_SCALE_SCAN,
        ...analyicsForAction(DISCARD_BODY_SCALE_SCAN),
    };
};

export const requestFailed = (error) => {
    return {
        type: REQUEST_FAILED,
        payload: {
            error,
        },
    };
};

type bodyScanErrorType = 'Permission' | 'BTDisabled' | 'GeneralError';
export interface IBodyScanError {
    type: bodyScanErrorType;
    message: string;
}

const bodyScaleError = (error: IBodyScanError) => {
    return {
        type: BODY_SCALE_ERROR,
        payload: {
            error,
        },
        ...analyicsForAction(BODY_SCALE_ERROR),
    };
};

export const bodyScaleProgressUpdate = (stage) => {
    return {
        type: BODY_SCALE_PROGRESS_UPDATE,
        payload: {
            stage,
        },
    };
};

export const startBodyScaleOnboarding = (userProfile: UserProfile) => {
    return {
        type: BODY_SCALE_ONBOARDING_START,
        payload: {
            userProfile,
        },
        ...analyicsForAction(BODY_SCALE_ONBOARDING_START),
    };
};

export const doBodyAssessment = (userProfile: UserProfile) => {
    return {
        type: BODY_SCALE_CAPTURE_METRIC_INFO,
        payload: {
            userProfile,
        },
        ...analyicsForAction(BODY_SCALE_CAPTURE_METRIC_INFO),
    };
};

export const showBodyTypeOptions = () => {
    return {
        type: SHOW_BODY_TYPE_OPTIONS,
        ...analyicsForAction(SHOW_BODY_TYPE_OPTIONS),
    };
};

export const setScanProgress = (progress) => {
    return {
        type: SET_SCAN_PROGRESS,
        payload: {
            progress,
        },
    };
};

export const setBodyAssessmentAnswer = (answer: string) => {
    return {
        type: SET_BODY_ASSESSMENT_ANSWER,
        payload: {
            answer,
        },
    };
};

export const getNextBodyAssessmentQuestion = () => {
    return {
        type: NEXT_BODY_ASSESSMENT_QUESTION,
    };
};

export const getPreviousBodyAssessmentQuestion = () => {
    return {
        type: PREVIOUS_BODY_ASSESSMENT_QUESTION,
    };
};

export const removeScaleRequest = () => {
    return {
        type: REMOVE_SCALE_REQUEST,
    };
}

export const removeScaleSuccess = () => {
    return {
        type: REMOVE_SCALE_SUCCESS,
    };
}

export const removeScaleFailure = () => {
    return {
        type: REMOVE_SCALE_FAILURE,
    };
};

export const initialiseBodyScan = (userProfile, useTestingStub = true, isScanContinuingFromWeightTarget = false) => {
    return async (dispatch) => {
        dispatch(setIsScanContinuingFromWeightTarget(isScanContinuingFromWeightTarget));
        dispatch(toggleBodyScanModal(true));
        dispatch(setTestingStub(useTestingStub));
        dispatch(startBodyScaleScanSetup(userProfile));
    };
};

export const startBodyScaleScanSetup = (userProfile) => {
    return (dispatch, getState) => {
        dispatch(bodyScaleScanStart(userProfile));
        const { bodyscale } = getState();
        const { hasMetricInfo, hasBodyComposition, hasBodyAssessment } = bodyscale.view;
        if (!(userProfile && hasMetricInfo && hasBodyComposition && hasBodyAssessment)) {
            dispatch(startBodyScaleOnboarding(userProfile));
        }
    };
};

const getPermissionsError = async (bluetoothService: IBluetoothService): Promise<IBodyScanError | null> => {
    if (Platform.OS === 'ios') {
        const bleUnauthorized = await bluetoothService.isBluetoothPermissionUnauthorized();
        if (bleUnauthorized) {
            const message = 'We need access to your device\'s Bluetooth to be able to connect to the scale.';
            return { message, type: 'Permission' };
        }
    }
    const bluetoothIsActive = await bluetoothService.verifyIfBluetoothIsActive();
    if (!bluetoothIsActive) {
        const errorMessage = 'Bluetooth needs to be enabled to use the scale.';
        return { message: errorMessage, type: 'BTDisabled' };
    }
    try {
        await bluetoothService.askForLocationPermissionIfNotGranted();
    } catch {
        const message = 'We need access to your device\'s location to be able to connect to the scale.';
        return { message, type: 'Permission' };
    }
    return null;
}

export const startBodyScaleScan = (userService)  => {
    return async (dispatch, getState) => {
        const bluetoothService: IBluetoothService = BluetoothServiceSingleton.getBluetoothService();
        const { bodyscale } = getState();
        const { useTestingStub } = bodyscale.view;
        const { metrics } = bodyscale.data;
        let subscription;
        try {
            Logger.leaveBreadcrumb(`starting body scan`);
            const scaleNativeEventEmitter = new NativeEventEmitter(NativeModules.HomeFatScale);
            subscription = scaleNativeEventEmitter.addListener(
                'ProgressUpdate',
                (update) => {
                    Logger.leaveBreadcrumb(`body scan progress update ${update.STAGE}`);
                    dispatch(bodyScaleProgressUpdate(update.STAGE));
                },
            );
            if (!useTestingStub && bluetoothService != null) {
                const permissionsError = await getPermissionsError(bluetoothService);
                if (permissionsError) {
                    dispatch(bodyScaleError(permissionsError));
                    return;
                }
            }

            let calculatedResult = await NativeModules.HomeFatScale.scanThenConnectThenCalculate({
                useTestingStub,
                ageInYears: Math.min(metrics.age, maxBodyScaleAge),
                genderStringEnum: metrics.gender,
                heightInCm: Math.min(metrics.height, maxBodyScaleHeight),
            });
            const scanDate = new Date();
            calculatedResult = { ...calculatedResult, scanDate };

            dispatch(requestBodyScaleScanStats(calculatedResult));
            const bodyScaleStats =
                await userService.calculateBodyScaleScanStatsForUser(
                    calculatedResult.weight, calculatedResult.bodyFatRate);
            dispatch(requestBodyScaleScanStatsSuccess(bodyScaleStats));
        } catch (error) {
            dispatch(bodyScaleError({ message: (error as Error).message, type: 'GeneralError' }));
            Logger.leaveBreadcrumb(`body scan error`);
            Logger.notify(error);
        } finally {
            if (subscription != null) {
                subscription.remove();
            }
        }
    };
};

export const saveBodyScaleScan = (userService, userProfile) => {
    return async (dispatch, getState) => {
        await saveBodyScan(dispatch, getState, userService, userProfile);
    };
};

export const saveBodyAssessment = (userService, userProfile: UserProfile, userProfileService) => {
    return async (dispatch, getState) => {
        dispatch(requestSaveBodyAssessment());
        const { bodyscale } = getState();
        const { metrics, bodyAssessment } = bodyscale.data;

        try {
            Logger.leaveBreadcrumb(`save body assessment`);
            const result = await userService.addBodyAssessmentForUser(bodyAssessment.results);
            const { gender, dateOfBirth, userProfileDob, useImperialUnit, height } = metrics;
            const hasGenderChange = gender && userProfile.gender !== gender;
            const hasUseImperialUnitChange = useImperialUnit == null ? true
             : userProfile.useImperialUnit !== useImperialUnit;
            const hasHeightChange = height && userProfile.height !== height;
            const hasDobChange = userProfileDob && userProfile.dateOfBirth !== userProfileDob;
            if (hasGenderChange || hasHeightChange || hasDobChange || hasUseImperialUnitChange) {
                if (hasGenderChange) { userProfile.gender = gender; }
                if (hasUseImperialUnitChange) { userProfile.useImperialUnit = useImperialUnit; }
                if (hasHeightChange) { userProfile.height = height; }
                if (hasDobChange) { userProfile.dateOfBirth = userProfileDob; }
            }
            await userService.updateUser(userProfile);
            await userProfileService.refreshCurrentUserProfile();
            dispatch(requestSaveBodyAssessmentSuccess(result.composition));
        } catch (error) {
            dispatch(requestFailed(error));
            Logger.leaveBreadcrumb(`save body assessment error`);
            Logger.notify(error);
        }
    };
};

export const overrideBodyCompositionAndConditioning =
    (userService, bodyCompositionType, athleteModeSelected: boolean, userProfile: UserProfile) => {
    return async (dispatch) => {
        dispatch(requestOverrideBodyCompositionAndConditioning());
        const athleteMode = athleteModeSelected ? CONDITIONED_ATHLETE_ANSWERS.CONDITIONED_ATHLETE_TRUE :
        CONDITIONED_ATHLETE_ANSWERS.CONDITIONED_ATHLETE_FALSE;
        try {
            await userService.addBodyAssessmentForUser({...userProfile.bodyAssessment, athleteMode});
            const response = await userService.overrideBodyCompositionForUser(bodyCompositionType);
            dispatch(requestOverrideBodyCompositionAndConditioningSuccess(
                response.bodyComposition, athleteMode));
        } catch (error) {
            dispatch(requestFailed(error));
            Logger.leaveBreadcrumb(`override body type error`);
            Logger.notify(error);
        }
    };
};

export const setIsScanContinuingFromWeightTarget = (isScanContinuingFromWeightTarget: boolean) => {
    return {
        type: SET_IS_SCAN_CONTINUING_FROM_WEIGHT_TARGET,
        payload: {
            isScanContinuingFromWeightTarget,
        },
    };
};
export async function saveBodyScan(dispatch: any, getState: any, userService: any, userProfile: any) {
    dispatch(requestSaveBodyScaleScan());
    const { bodyscale } = getState();
    const { rawScan, scaleScanStats } = bodyscale.data;

    try {
        Logger.leaveBreadcrumb(`save body scan`);

        const datestamp = new Date().valueOf();
        const timezone = moment.tz.guess();

        const state = getState();
        const target  = weightTargetInfoFromGoalSelector(state);

        const scanDateString = moment(rawScan.scanDate).format('YYYY-MM-DD HH:mm:ss');

        if (target) {
            const targetMetricValues : ITargetMetricValues = {
                muscleMass: scaleScanStats.calculatedMuscleMassMeterKg,
                bodyFat: scaleScanStats.calculatedBodyFat,
                bodyWeight: rawScan.weight,
            }

            const result = await userService.reviewWeightAndGetTargetInfo(
                rawScan.weight, userProfile.PALValue, datestamp, timezone, UserTargetTypes[target.targetType], rawScan, targetMetricValues);
            dispatch(requestWeightTargetInfo(rawScan.weight));
            dispatch(requestWeightTargetInfoSuccess(result, target.targetType));
            dispatch(requestBodyMetricStatsSuccess(result));
            dispatch(setWeightTargetStep(WeightTargetStep.BodyWeightSummary));
            dispatch(toggleWeightTargetModal(true));
            dispatch(requestSaveBodyScaleScanSuccess(result.bodyMetricSet));

            if (result.weightTargetInfo.target.reached) {
                dispatch(resetTargetRequest());
                const resetResult = await userService.getTargetAndBodyMetricSetForUser(datestamp, timezone);
                dispatch(requestBodyMetricStatsSuccess(resetResult));
                dispatch(resetTargetRequestSuccess(resetResult));
            }
        } else {
            const response = await userService.addBodyScaleScanForUser(datestamp, timezone, scanDateString, rawScan);
            const formattedResonse = {bodyMetricSet: response};
            dispatch(requestBodyMetricStatsSuccess(formattedResonse));
            dispatch(requestSaveBodyScaleScanSuccess(response));
            dispatch(toggleBodyScanModal(false));
        }

    } catch (error) {
        dispatch(requestFailed(error));
        Logger.leaveBreadcrumb(`save body scan error`);
        Logger.notify(error);
    }
}

export const removeBodyScale = (
    shouldRemoveScale: boolean,
    userServiceClient: UserServiceClient,
    userProfileService: UserProfileService,
  ) => {
    return async (dispatch, getState) => {
        try {
            dispatch(removeScaleRequest());
            if (shouldRemoveScale) {
                await userServiceClient.removeBodyScaleForUser();
                await userProfileService.refreshCurrentUserProfile();
                dispatch(toggleBodyScanModal(false));
            } else {
                dispatch(startBodyScaleScan(userServiceClient));
            }

            dispatch(removeScaleSuccess());
        } catch (error) {
            alert(error)
            dispatch(removeScaleFailure());
        }
    };
};
